import React, { useState } from 'react';
import { ArrowLeft, Minus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

interface LeagueSettingsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  leagueName: string;
}

interface User {
  id: string;
  username: string;
}

const LeagueSettingsOverlay: React.FC<LeagueSettingsOverlayProps> = ({
  isOpen,
  onClose,
  leagueName,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRemoveUserConfirm, setShowRemoveUserConfirm] = useState<string | null>(null);
  const [leaderboardSettings, setLeaderboardSettings] = useState({
    today: false,
    week: true,
    month: true,
    season: true,
  });

  const users: User[] = [
    { id: '1', username: 'HockeyHero92' },
    { id: '2', username: 'PuckMasterX' },
    { id: '3', username: 'IceQueen77' },
    { id: '4', username: 'GoalieGuru88' },
    { id: '5', username: 'StickWizard45' },
    { id: '6', username: 'FrozenFlame99' },
    { id: '7', username: 'SkateAndScore' },
  ];

  const handleLeaderboardChange = (key: keyof typeof leaderboardSettings) => {
    setLeaderboardSettings(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleDeleteGroup = () => {
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleRemoveUser = (_userId: string, _username: string) => {
    setShowRemoveUserConfirm(null);
  };

  if (!isOpen) return null;

  return (
    <div
      data-test-id="league-settings-overlay"
      className="absolute inset-0 z-50 bg-app-background rounded-2xl overflow-hidden"
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <header className="bg-app-background border-b border-border/30 p-4">
          <div className="flex items-center justify-between">
            <button
              data-test-id="back-btn"
              onClick={onClose}
              className="flex items-center gap-2 text-text-primary hover:text-text-secondary transition-smooth"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <div className="w-[60px]"></div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="text-center mb-6">
            <h1 className="text-text-primary font-bold text-xl mb-2">SETTINGS</h1>
            <h2 className="text-text-primary font-bold text-lg">{leagueName}</h2>
          </div>

          {/* Show Leaderboards Section */}
          <div className="mb-6">
            <h3 className="text-text-primary font-medium text-center mb-4">Show Leaderboards</h3>
            <div className="flex justify-center gap-6">
              {Object.entries(leaderboardSettings).map(([key, checked]) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox
                    id={key}
                    checked={checked}
                    onCheckedChange={() =>
                      handleLeaderboardChange(key as keyof typeof leaderboardSettings)
                    }
                  />
                  <label
                    htmlFor={key}
                    className="text-text-primary font-medium capitalize cursor-pointer"
                  >
                    {key}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Delete Group Button */}
          <div className="flex justify-center mb-6">
            <Button
              data-test-id="delete-league-btn"
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              className="px-8"
            >
              Delete Group
            </Button>
          </div>

          {/* Users List */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-text-primary font-medium">{users.length} Users</span>
              <span className="text-text-primary font-medium">Remove</span>
            </div>

            <div className="space-y-2">
              {users.map(user => (
                <div
                  key={user.id}
                  data-test-id={`league-user-${user.id}`}
                  className="flex items-center justify-between py-3 px-4 bg-game-card rounded-lg"
                >
                  <span className="text-text-primary font-medium">{user.username}</span>
                  <button
                    data-test-id={`remove-user-btn-${user.id}`}
                    onClick={() => setShowRemoveUserConfirm(user.id)}
                    className="w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-smooth"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Delete Group Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-60">
            <div
              data-test-id="delete-league-modal"
              className="bg-app-background rounded-xl p-6 w-full max-w-sm"
            >
              <div className="flex justify-end mb-4">
                <button
                  data-test-id="close-delete-modal-btn"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="text-text-secondary hover:text-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="text-center mb-6">
                <h3 className="text-text-primary font-bold text-lg mb-3">DELETE "{leagueName}"?</h3>
                <p className="text-text-secondary">
                  All friends will be removed from the leaderboard.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  data-test-id="confirm-delete-league-btn"
                  variant="destructive"
                  onClick={handleDeleteGroup}
                  className="w-full"
                >
                  Delete
                </Button>
                <Button
                  data-test-id="cancel-delete-league-btn"
                  variant="secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Remove User Confirmation Modal */}
        {showRemoveUserConfirm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-60">
            <div
              data-test-id="remove-user-modal"
              className="bg-app-background rounded-xl p-6 w-full max-w-sm"
            >
              <div className="flex justify-end mb-4">
                <button
                  data-test-id="close-remove-user-modal-btn"
                  onClick={() => setShowRemoveUserConfirm(null)}
                  className="text-text-secondary hover:text-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="text-center mb-6">
                <h3 className="text-text-primary font-bold text-lg mb-3">
                  REMOVE "{users.find(u => u.id === showRemoveUserConfirm)?.username}"?
                </h3>
                <p className="text-text-secondary">
                  All friends will be removed from the leaderboard.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  data-test-id="confirm-remove-user-btn"
                  variant="pickem"
                  onClick={() =>
                    handleRemoveUser(
                      showRemoveUserConfirm,
                      users.find(u => u.id === showRemoveUserConfirm)?.username || ''
                    )
                  }
                  className="w-full"
                >
                  Remove Player
                </Button>
                <Button
                  data-test-id="cancel-remove-user-btn"
                  variant="secondary"
                  onClick={() => setShowRemoveUserConfirm(null)}
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeagueSettingsOverlay;
